import { useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWebsocket } from "@finitless/shared/contexts";
import { useApiStore } from "@finitless/shared/stores";
import { userInfo } from "@finitless/shared/stores";

const normalizeSiteUpdate = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  const siteId = payload.site_id ?? payload.id;
  if (!siteId) return null;

  if (payload.changes && typeof payload.changes === "object") {
    return { siteId, sitePatch: payload.changes };
  }

  const sitePatch = { ...payload };
  delete sitePatch.site_id;
  delete sitePatch.id;

  return { siteId, sitePatch };
};

const mergeSiteData = (currentSite, sitePatch) => {
  const safeCurrent = currentSite || {};
  const safePatch = sitePatch || {};

  return {
    ...safeCurrent,
    ...safePatch,
    deployment_provider_settings: {
      ...(safeCurrent.deployment_provider_settings || {}),
      ...(safePatch.deployment_provider_settings || {}),
    },
  };
};

/**
 * Hook for handling real-time site updates via websockets
 * Updates agent's site SSL information when site updates are received
 *
 * @param {Object} agent - The agent object containing site information
 * @returns {Object} Object with connection status
 */
export const useAgentSiteSocketUpdates = (agent) => {
  const queryClient = useQueryClient();
  const { socket, isConnected } = useWebsocket();
  const currentProject = useApiStore((state) => state.project);
  const user = userInfo();
  const site = agent?.site || null;

  const agentRef = useRef(agent);
  const siteRef = useRef(site);

  useEffect(() => {
    agentRef.current = agent;
    siteRef.current = site;
  }, [agent, site]);

  /**
   * Handle site update from websocket
   * Updates the agent's site object in cache with new SSL data
   */
  const handleSiteUpdate = useCallback(
    (data) => {
      const currentSite = siteRef.current;
      const currentAgent = agentRef.current;
      const normalizedUpdate = normalizeSiteUpdate(data);
      if (!normalizedUpdate || !currentAgent?.id) return;

      if (normalizedUpdate.siteId !== currentSite?.id) return;

      queryClient.setQueryData(["ai-agent-site", currentAgent.id], (oldSite) =>
        mergeSiteData(oldSite, normalizedUpdate.sitePatch),
      );

      queryClient.setQueriesData({ queryKey: ["ai-agents"] }, (oldData) => {
        if (!oldData?.agents) return oldData;

        return {
          ...oldData,
          agents: oldData.agents.map((a) => {
            if (a.id !== currentAgent.id) return a;

            return {
              ...a,
              site: mergeSiteData(a.site, normalizedUpdate.sitePatch),
            };
          }),
        };
      });
    },
    [queryClient],
  );

  useEffect(() => {
    if (!socket || !isConnected || !agent?.id) return;

    socket.join({
      room: "sites",
      project: currentProject,
      role: "manager",
      user_id: user?.id,
    });

    socket.on("update_site", handleSiteUpdate);

    return () => {
      socket.off("update_site", handleSiteUpdate);
      socket.leave({
        room: "sites",
        project: currentProject,
        role: "manager",
      });
    };
  }, [
    socket,
    isConnected,
    agent?.id,
    currentProject,
    user?.id,
    handleSiteUpdate,
  ]);

  return {
    isConnected,
  };
};

export default useAgentSiteSocketUpdates;
